import React, { useCallback, useContext, useEffect, useState } from "react";
import { Crown, RefreshCcw, Search, ShieldCheck, Users } from "lucide-react";
import { AuthContext } from "./AuthContext";
import { API_BASE_URL } from "./api";
import { Button, MetricCard, SectionHeader, SurfaceCard } from "./components/designSystem";

const PLAN_DURATION_OPTIONS = [
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "12m", label: "1 year" },
];

function formatDate(value) {
  if (!value) {
    return "No expiry set";
  }

  return new Date(value).toLocaleDateString("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function AdminPanel() {
  const { token, user, syncSession, logout, setRefresh } = useContext(AuthContext);
  const isAdmin = Boolean(user?.isAdmin);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState({});
  const [drafts, setDrafts] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const loadUsers = useCallback(async () => {
    if (!isAdmin || !token) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to fetch users.");
      }
      setUsers(data.users || []);
      setDrafts(
        (data.users || []).reduce((result, entry) => {
          result[entry._id] = {
            plan: entry.plan,
            duration: entry.plan === "free" ? "1m" : "1m",
          };
          return result;
        }, {})
      );
    } catch (loadError) {
      setError(loadError.message || "Failed to fetch users.");
    }

    setLoading(false);
  }, [isAdmin, token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredUsers = normalizedQuery
    ? users.filter((entry) => {
        const username = (entry.username || "").toLowerCase();
        const email = (entry.email || "").toLowerCase();
        const plan = (entry.plan || "").toLowerCase();
        return username.includes(normalizedQuery) || email.includes(normalizedQuery) || plan.includes(normalizedQuery);
      })
    : users;

  const handlePlanChange = async (id) => {
    const selectedDraft = drafts[id] || { plan: "free", duration: "1m" };
    setUpdating((current) => ({ ...current, [id]: true }));
    setError("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/user/${id}/plan`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: selectedDraft.plan,
          duration: selectedDraft.plan === "free" ? undefined : selectedDraft.duration,
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Update failed");
      }

      setUsers((current) => current.map((entry) => entry._id === id ? data.user : entry));
      setDrafts((current) => ({
        ...current,
        [id]: {
          plan: data.user.plan,
          duration: selectedDraft.duration,
        },
      }));

      if (user && user._id === id) {
        const userRes = await fetch(`${API_BASE_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (userRes.status === 401) {
          logout();
          return;
        }
        const userData = await userRes.json();
        if (userData.user && userData.token) {
          syncSession(userData);
        }
        if (setRefresh) {
          setRefresh((value) => value + 1);
        }
      }
    } catch (updateError) {
      setError(updateError.message || "Failed to update user plan.");
    }

    setUpdating((current) => ({ ...current, [id]: false }));
  };

  if (!isAdmin) {
    return <p className="mt-5 text-red-500">Access denied. Admins only.</p>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_22%),radial-gradient(circle_at_80%_20%,_rgba(251,191,36,0.18),_transparent_18%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.12),_transparent_22%),radial-gradient(circle_at_80%_20%,_rgba(251,191,36,0.14),_transparent_18%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Admin Panel"
          title="Manage user plans"
          description="Use this page for direct manual entitlement changes. Payment-request approvals stay on the separate pending-payments screen."
          action={
            <Button type="button" variant="secondary" onClick={loadUsers}>
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
          }
        />
        <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by username, email, or plan"
              className="w-full rounded-full border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <MetricCard label="Users" value={String(users.length)} helper="Accounts currently loaded." />
          <MetricCard label="Free" value={String(users.filter((entry) => entry.plan === "free").length)} helper="Users on the free tier." />
          <MetricCard label="Pro" value={String(users.filter((entry) => entry.plan === "pro").length)} helper="Users with Pro access." />
          <MetricCard label="Enterprise" value={String(users.filter((entry) => entry.plan === "enterprise").length)} helper="Users with Enterprise access." />
        </div>
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      {loading ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading users...</p>
        </SurfaceCard>
      ) : (
        <div className="space-y-4">
          {filteredUsers.length ? filteredUsers.map((entry) => (
            <SurfaceCard key={entry._id}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-900 dark:bg-gray-900 dark:text-white">
                    {entry.plan === "enterprise" ? <Crown className="h-5 w-5" /> : entry.isAdmin ? <ShieldCheck className="h-5 w-5" /> : <Users className="h-5 w-5" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">{entry.username}</p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{entry.email}</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.28em] text-slate-400">Current plan: {entry.plan}</p>
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      {entry.plan === "free" ? "No active paid expiry" : `Access ends ${formatDate(entry.proPlanEnd)}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <select
                    value={(drafts[entry._id] || { plan: entry.plan }).plan}
                    onChange={(event) => setDrafts((current) => ({
                      ...current,
                      [entry._id]: {
                        plan: event.target.value,
                        duration: current[entry._id]?.duration || "1m",
                      },
                    }))}
                    disabled={updating[entry._id]}
                    className="rounded-full border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
                  >
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  {(drafts[entry._id]?.plan || entry.plan) !== "free" ? (
                    <select
                      value={drafts[entry._id]?.duration || "1m"}
                      onChange={(event) => setDrafts((current) => ({
                        ...current,
                        [entry._id]: {
                          plan: current[entry._id]?.plan || entry.plan,
                          duration: event.target.value,
                        },
                      }))}
                      disabled={updating[entry._id]}
                      className="rounded-full border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
                    >
                      {PLAN_DURATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  ) : null}
                  <Button type="button" variant="secondary" disabled={updating[entry._id]} onClick={() => handlePlanChange(entry._id)}>
                    {updating[entry._id] ? "Updating..." : "Apply"}
                  </Button>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{updating[entry._id] ? "Updating plan..." : "Paid plans now require a duration and expire automatically."}</span>
                </div>
              </div>
            </SurfaceCard>
          )) : (
            <SurfaceCard>
              <p className="text-sm text-slate-500 dark:text-slate-400">No users match your search.</p>
            </SurfaceCard>
          )}
        </div>
      )}
    </div>
  );
}
